import React, { PropsWithChildren } from 'react'

import { ReactFC } from 'common/types/ReactFC'
import { TPlanSummary } from 'common/cms-types'
import FeatureItem from 'components/FeatureItem'
import MediaCard from './common/MediaCard'
import SubscriptionPricing from 'components/SubscriptionPricing'
import { ButtonType } from '../../../types/global'
import { getMailCountDescription } from 'common/utils/getMailCountString'
import { Tenant } from 'common/tenant'

const getAUFeatures = () => [
  {
    description:
      'Simply register it with ASIC as your registered office address.',
    title: 'A professional address.',
  },
  {
    description:
      'We scan and send your business mail to your inbox, mobile app, and customer portal within one business day.',
    title: 'Mail handling straight to your inbox within 1 day.',
  },
  {
    description:
      'Get business mail summaries, tags, and a fully searchable document hub — all in one place.',
    title: 'AI-powered mail services to streamline your business.',
  },
  {
    description: getMailCountDescription(),
    title: 'Digitally stored safely and securely.',
  },
  {
    description:
      'Use it as your official registered company address, keeping your personal address off the ASIC register while projecting a professional image.',
    title: 'ASIC compliance.',
  },
  {
    description: 'Simplicity for your mail and more.',
    title: 'One plan, 10-minute setup, monthly cancellation.',
  },
]

const getUKFeatures = () => [
  {
    description:
      'Simply register it with Companies House as your registered office address. The plan also offers a director service address add-on, offering a prestigious business address while ensuring privacy and easy access to official correspondence.',
    title: 'A professional address.',
  },
  {
    description:
      'We scan and send your business mail to your inbox, mobile app, and customer portal within one business day.',
    title: 'Mail handling straight to your inbox within 1 day.',
  },
  {
    description:
      'Get business mail summaries, tags, and a fully searchable document hub — all in one place.',
    title: 'AI-powered mail services to streamline your business.',
  },
  {
    description: getMailCountDescription(),
    title: 'Digitally stored safely and securely.',
  },
  {
    description:
      'Use it as your official registered company address, keeping your personal address off the Companies House register while projecting a professional image.',
    title: 'Companies House compliance.',
  },
  {
    description: 'Simplicity for your mail and more.',
    title: 'One plan, 10-minute setup, monthly cancellation.',
  },
]

type FeaturesCardType = {
  className?: string
  planSummary: TPlanSummary
  tenant?: Tenant
}

const FeaturesCard: ReactFC<PropsWithChildren<FeaturesCardType>> = ({
  className = '',
  planSummary,
  tenant = Tenant.UK,
}) => {
  const au = tenant === Tenant.AU
  const features = au ? getAUFeatures() : getUKFeatures()
  return (
    <>
      <MediaCard
        className={className}
        layout={{ mobile: 'content-top-media-bottom' }}
        imageContainerWidth='52%'
        imageFitPreset='contain-center'
        image={{
          alt: 'HaiRo™ mail service with robot mascot and phone displaying mail interface',
          desktop: {
            dimensions: {
              height: 746,
              width: 617,
            },
            src: '/images/new/robot-mail-preview.png',
          },
          mobile: {
            dimensions: {
              height: 329,
              width: 272,
            },
            src: '/images/new/robot-mail-preview.png',
          },
        }}
        footerContent={
          <div className='px-4 pb-5 d-lg-none'>
            <SubscriptionPricing
              planSummary={planSummary}
              buttonType={ButtonType.FEATURES_CARD}
              tenant={tenant}
            />
          </div>
        }
      >
        <div className='d-flex flex-column px-4 pt-8 pb-5 ps-lg-20 pe-lg-8 pt-lg-10 pb-lg-16'>
          <h3 className='fs-fluid-24-32 fw-semibold mb-8'>
            Our{' '}
            <span className='color-pink2 fw-semibold'>
              virtual office address package
            </span>{' '}
            includes:
          </h3>

          <div className='d-flex flex-column gap-4 fs-fluid-16-20'>
            {features.map((feature) => (
              <FeatureItem
                key={feature.title}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </div>
        </div>
      </MediaCard>
      <div className='d-none d-lg-flex justify-content-center text-center mt-15'>
        <SubscriptionPricing
          planSummary={planSummary}
          buttonType={ButtonType.FEATURES_CARD}
          tenant={tenant}
        />
      </div>
    </>
  )
}

export default FeaturesCard
